import { useContext, useState, type ReactNode } from 'react';

import { ShowErrorsContext } from './showErrorsContext';

/**
 * Keeps the "Next was pressed" error signal off an entry the advisor adds AFTERWARDS.
 *
 * ShowErrorsContext is a step-wide tick: once Next is refused, every field under the step
 * re-runs its blur check and paints what is missing. A repeatable row or section instance added
 * at that point mounts straight into that signal, so its blank required cells are red before the
 * advisor has typed a character in them — the form scolding them for a row they just asked for.
 *
 * Each entry captures the tick it was born under and reports zero until the tick moves PAST it,
 * which only happens on the next Next. So:
 *   - entries present when Next was pressed show their errors, unchanged;
 *   - an entry added afterwards starts clean, and speaks up on the following Next.
 *
 * Display only — FormShell's completeness gate is untouched, so a blank new row still holds Next.
 */
export function FreshEntryErrorScope({ children }: { children: ReactNode }) {
  const raised = useContext(ShowErrorsContext);
  const [bornTick] = useState(raised);
  return (
    <ShowErrorsContext.Provider value={raised > bornTick ? raised : 0}>
      {children}
    </ShowErrorsContext.Provider>
  );
}
